import React, { ButtonHTMLAttributes, useRef, useCallback, useState } from 'react';
import { FormHandles } from '@unform/core';
import { Form } from '@unform/web';
import { IoMdCloseCircle } from 'react-icons/io';
import * as Yup from 'yup';

import Button from '../../../../components/Button';
import Input from '../../../../components/Input';

import { Container } from './styles';

import { useToast } from '../../../../hooks/toast';

import api from '../../../../services/api';
import { format } from 'date-fns';

interface InputProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  show?: boolean;
  closeFunction: (arg0: boolean) => void;
  deviceId: string;
  url: string;
}

const DownloadDeviceExcel: React.FC<InputProps> = ({ show, closeFunction, deviceId, url }) => {
  const formRef = useRef<FormHandles>(null);

  const { addToast } = useToast();

  const [loading, setLoading] = useState(false);
  
  const submitMessage = useCallback(
    async formData => {
      setLoading(true);
  
      try {
        formRef.current?.setErrors({});
  
        const schema = Yup.object().shape({
          initialDate: Yup.date().required('Data inicial obrigatoria'),
          finalDate: Yup.date()
            .required('Data final obrigatoria')
            .test(
              'is-after-start',
              'Data final deve ser maior que a data inicial',
              function (value) {
                const { initialDate } = this.parent;
                return initialDate && value && value > initialDate;
              }
            ).test(
              'is-day-greater-than-60',
              'O periodo não deve ser maior que 60 dias',
              function (value) {
                const { initialDate } = this.parent;
                const differenceInMilliseconds = value.getTime() - initialDate.getTime();
                const differenceInDays = differenceInMilliseconds / (1000 * 60 * 60 * 24);
                return differenceInDays < 60;
              }
            ),
        });
  
        await schema.validate(formData, { abortEarly: false });
  
        const { finalDate, initialDate } = formData;
        const dateFinal = new Date(finalDate);
        const formatfinaldate = format(dateFinal, 'y-MM-dd HH:mm:ss.SSS');
        const dateinitial = new Date(initialDate);
        const formatinitialdate = format(dateinitial, 'y-MM-dd HH:mm:ss.SSS');
  
        // faz o request pegando o Blob (CSV)
        const response = await api.get(url, {
          params: { device_id: deviceId, finalDate: formatfinaldate, initialDate: formatinitialdate },
          responseType: 'blob',
        });
        
        const blob = response.data;
        const link = document.createElement('a');
        const fileURL = window.URL.createObjectURL(blob);
        link.href = fileURL;
        // tenta pegar o nome do arquivo a partir do header, ou usa um fallback
        const fileName = `relatorio_${deviceId}_${format(new Date(),'y-MM-dd_HH_mm_ss')}.xlsx`;
        
        link.setAttribute('download', fileName);
        document.body.appendChild(link);
        link.click();
        link.remove();
  
        addToast({
          title: 'Sucesso',
          description: 'Arquivo exportado com sucesso!',
          type: 'success',
        });
        formRef.current?.reset();
        closeFunction(false); // Fecha o componente após sucesso
      } catch (error) {
        
        if (error instanceof Yup.ValidationError) {
          const validationErrors: { [key: string]: string } = {};
          error.inner.forEach(err => {  
            validationErrors[err.path || ''] = err.message;
          });
          formRef.current?.setErrors(validationErrors);
          return addToast({
            type: 'error',
            title: 'Erro na validação',
            description: 'Por favor, verifique os campos e tente novamente.',
          });
        }
        if (error =="Error: Request failed with status code 400") {
          return addToast({
            type: 'info',
            title: '',
            description: 'Nenhum valor encontrado para esse período!',
          })
        }
        return addToast({
          type: 'error',
          title: 'Erro ao criar Arquivo',
          description: (error as any).response ? `${(error as any).response.data.message}` : `${error}`,
        });
      } finally {
        setLoading(false);
      }
    },
    [addToast, deviceId, url, closeFunction],
  );
  

  const closeHandler = useCallback(async () => {
    formRef.current?.setErrors({});
    formRef.current?.reset();
    return closeFunction(false);
  }, [closeFunction, addToast]);

  return (
    <Container show={show} sending={loading}>
      <button className="closeMessageButton" type="button" onClick={closeHandler}>
        <IoMdCloseCircle />
      </button>
      <h2>Selecione um intervalo de data</h2>
      <Form onSubmit={submitMessage} id="form" ref={formRef}>
        <p>Data Inicial:</p>
        <Input name="initialDate" type="datetime-local" />

        <p>Data final:</p>
        <Input name="finalDate" type="datetime-local" />

        <Button type="submit">{loading ? 'Criando...' : 'Download'}</Button>
      </Form>
    </Container>
  );
};

export default DownloadDeviceExcel;
